import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { PhotoResponse } from "@egalregal/shared";
import { useAppStore, readableContainers } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import { log } from "../../logger";
import { getMirroredItems } from "../search/catalogStore";
import { containerEntryLabel } from "../catalogs/entryLabel";
import { collectImageCandidates, searchImagesByAppearance } from "./imageRanking";
import { imageSearchService } from "./imageSearch";
import { PhotoGrid } from "./PhotoGrid";

/**
 * The gallery's "by appearance" mode (ticket 09): the user describes a photo
 * and sees the best-matching photos ranked in-browser against the server-
 * produced embeddings already mirrored to the client. The UForm text encoder
 * downloads lazily on first use (progress bar), and the existing text search /
 * browse mode stay available.
 */
export function GalleryAppearanceSearch() {
  const t = useT();
  const containers = useAppStore((s) => s.containers);
  const catalogContainers = useAppStore((s) => s.catalogContainers);
  const entries = useAppStore((s) => s.entries);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const selectedEntryId = useAppStore((s) => s.selectedEntryId);
  const online = useAppStore((s) => s.online);
  // Rank every readable entry's photos (ticket 12), falling back to the
  // entry-scoped slice until the union has loaded.
  const pool = readableContainers({ catalogContainers, containers });
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<"offline" | "failed" | null>(null);
  const [results, setResults] = useState<PhotoResponse[]>([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    imageSearchService.onProgress(setProgress);
    return () => imageSearchService.onProgress(undefined);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setSearched(false);
      setError(null);
      setLoading(false);
      setProgress(null);
      return;
    }

    let stale = false;
    setSearched(false);
    setLoading(true);
    setError(null);
    // Indeterminate until the model layer reports byte progress: a cold miss
    // (network fetch) drives the determinate bar, while a warm Browser-model-
    // store hit keeps the loading indicator through the store read + session
    // init with no fake determinate progress.
    setProgress(null);

    const timer = setTimeout(() => {
      void (async () => {
        try {
          // Offline, the search only works if the encoder is already resident
          // from an earlier search — otherwise surface the offline state instead
          // of hanging on a network fetch.
          if (!online && !(await imageSearchService.ready)) {
            if (!stale) {
              setError("offline");
              setLoading(false);
              setProgress(null);
            }
            return;
          }
          const candidates = collectImageCandidates(pool, getMirroredItems(), (id) =>
            containerEntryLabel(pool, entries, sharedWithMe, id, selectedEntryId),
          );
          const photos = await searchImagesByAppearance(q, candidates, (text) =>
            imageSearchService.embed(text),
          );
          if (stale) return;
          setResults(photos);
          setSearched(true);
        } catch (err) {
          log.warn({ err }, "[image-search] appearance search failed");
          if (!stale) setError("failed");
        } finally {
          if (!stale) {
            setLoading(false);
            setProgress(null);
          }
        }
      })();
    }, 250);

    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [query, pool, online, entries, sharedWithMe, selectedEntryId]);

  const noMatch = searched && !loading && results.length === 0 && !error;

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        placeholder={t("appearancePlaceholder")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        inputProps={{ "aria-label": t("appearancePlaceholder") }}
        autoComplete="off"
        sx={{ mb: 1 }}
      />

      {loading && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress
            variant={progress !== null ? "determinate" : "indeterminate"}
            value={progress !== null ? Math.round(progress * 100) : undefined}
            aria-label={t("appearanceDownloading")}
          />
          <Typography variant="caption" color="text.secondary">
            {progress !== null ? t("appearanceDownloading") : t("appearanceLoading")}
          </Typography>
        </Box>
      )}

      {error === "offline" && (
        <Typography variant="body2" color="text.secondary" data-testid="appearance-offline">
          {t("appearanceOffline")}
        </Typography>
      )}

      {error === "failed" && (
        <Typography variant="body2" color="error" data-testid="appearance-error">
          {t("appearanceError")}
        </Typography>
      )}

      {noMatch && (
        <Typography variant="body2" color="text.secondary" data-testid="appearance-nomatch">
          {t("appearanceNoMatches")}
        </Typography>
      )}

      {results.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {results.length}
          </Typography>
        </Stack>
      )}

      <PhotoGrid photos={results} />
    </Box>
  );
}
